'use client';

import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { Sparkles, Send } from 'lucide-react';
import { clsx } from 'clsx';
import SectionShell from './ui/Section';
import { SectionHeader } from './ui/SectionHeader';
import PrivacyCheckbox from './form/PrivacyCheckbox';
import { SlideInToast } from './ui/SlideInToast';

const GAS_ENDPOINT = import.meta.env.VITE_GAS_ENDPOINT as string;

const MemoizedSectionHeader = memo(SectionHeader);
const MemoizedPrivacyCheckbox = memo(PrivacyCheckbox);
const MemoizedSlideInToast = memo(SlideInToast);

export default function QuestionForm() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<null | { ok: boolean; msg: string }>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // Оптимизация: мемоизация обработчиков событий
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleToastClose = useCallback(() => setToastOpen(false), []);
  
  const handleAgreeChange = useCallback((checked: boolean) => setAgree(checked), []);

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    if (!GAS_ENDPOINT) {
      e.preventDefault();
      setSubmitStatus({ ok: false, msg: 'No GAS endpoint (VITE_GAS_ENDPOINT).' });
      setToastOpen(true);
      return;
    }
    if (!formData.name || !formData.email || !formData.message) {
      e.preventDefault();
      setSubmitStatus({ ok: false, msg: 'Please fill in all required fields.' });
      setToastOpen(true);
      return;
    }
    if (!agree) {
      e.preventDefault();
      setSubmitStatus({ ok: false, msg: 'Please confirm you agree to the data notice.' });
      setToastOpen(true);
      return;
    }
    setSubmitting(true);
    setSubmitStatus(null);
  }, [formData.name, formData.email, formData.message, agree]);

  useEffect(() => {
    const onLoad = () => {
      if (!submitting) return;
      setSubmitting(false);
      setSubmitStatus({ ok: true, msg: "Thank you! We'll respond shortly." });
      setToastOpen(true);
      setFormData({ name: '', email: '', message: '' });
      setAgree(false);
    };
    const iframe = iframeRef.current;
    if (iframe) iframe.addEventListener('load', onLoad);
    return () => { if (iframe) iframe.removeEventListener('load', onLoad); };
  }, [submitting]);

  useEffect(() => {
    const timer = setTimeout(() => firstFieldRef.current?.focus({ preventScroll: true } as any), 30);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <SectionShell id="contact" bgClass="bg-navy-top">
        <style>{`
          input:-webkit-autofill,
          textarea:-webkit-autofill {
            -webkit-box-shadow: 0 0 0px 1000px rgba(255,255,255,0.06) inset !important;
            -webkit-text-fill-color: #fff !important;
            caret-color: #fff !important;
            transition: background-color 9999s ease-in-out 0s;
          }
          .card-wrap:hover .card-ambient,
          .card-wrap:focus-within .card-ambient {
            opacity: .9;
            animation: formPulse 4s ease-in-out infinite;
            will-change: transform, filter;
          }
          .card-ambient { 
            opacity: 0; 
            transition: opacity .35s ease;
            will-change: opacity;
          }
          @keyframes formPulse {
            0%, 100% { filter: blur(22px); transform: scale(1) translateZ(0); opacity:.65; }
            50%       { filter: blur(30px); transform: scale(1.03) translateZ(0); opacity:1; }
          }
          .input-base {
            width: 100%;
            padding: 0.75rem 1rem;
            border-radius: 0.75rem;
            background: rgba(255,255,255,0.06);
            color: #fff;
            border: 1px solid rgba(255,255,255,0.10);
            transition: box-shadow .2s ease, border-color .2s ease, background .2s ease;
            will-change: border-color, background;
          }
          .input-base::placeholder { color: rgba(255,255,255,0.45); }
          .input-base:focus {
            outline: none;
            border-color: rgba(255,255,255,0.28);
            background: rgba(255,255,255,0.08);
            box-shadow: 0 0 0 2px rgba(249,113,22,.35), 0 0 0 6px rgba(236,72,153,.18);
          }
        `}</style>

        <MemoizedSectionHeader
          eyebrowText="Contact Us"
          eyebrowIcon={Sparkles}
          title="Get in Touch"
          subtitle="Have a question, idea, or partnership proposal? Send us a note — we'll get back to you soon."
          titleGradientClassName="bg-gradient-to-r from-[#7dd3fc] via-[#a78bfa] to-[#ec4899]"
          textColorClass="text-white"
          align="center"
        />

        <div className="max-w-2xl mx-auto card-wrap relative">
          <div
            className="card-ambient pointer-events-none absolute -inset-6 rounded-[1.75rem] blur-2xl"
            style={{
              background:
                'radial-gradient(130% 130% at 25% 20%, rgba(13,39,61,0.45), rgba(214,92,48,0.35) 95%, transparent 100%)',
              animation: 'formPulse 4s ease-in-out infinite'
            }}
          />

          <div className="relative rounded-2xl bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/30 overflow-hidden">
            <iframe name="contact_iframe" ref={iframeRef} style={{ display: 'none' }} />

            <form
              action={GAS_ENDPOINT}
              method="POST"
              target="contact_iframe"
              onSubmit={handleSubmit}
              className="p-6 sm:p-8 space-y-6"
            >
              <input type="hidden" name="sheet" value="Questions" />

              {/* Name */}
              <div>
                <label className="font-montserrat font-semibold text-white/90 block mb-2" htmlFor="name">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-base"
                  autoComplete="name"
                  ref={firstFieldRef}
                />
              </div>

              {/* Email */}
              <div>
                <label className="font-montserrat font-semibold text-white/90 block mb-2" htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-base"
                  autoComplete="email"
                />
              </div>

              {/* Message */}
              <div>
                <label className="font-montserrat font-semibold text-white/90 block mb-2" htmlFor="message">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us more about your project or question..."
                  value={formData.message}
                  onChange={handleChange}
                  className="input-base resize-none"
                />
              </div>

              {/* Checkbox */}
              <MemoizedPrivacyCheckbox
                checked={agree}
                onChange={handleAgreeChange}
                label="I agree for ZeroClub to process my data only for communication purposes."
              />

              <button
                type="submit"
                disabled={submitting}
                className={clsx(
                  'mt-2 w-full font-montserrat font-semibold text-white px-8 py-4 rounded-xl text-lg transition-all duration-300',
                  'bg-[#d65c30]',
                  'shadow-lg shadow-[#d65c30]/30 hover:shadow-[0_0_40px_rgba(214,92,48,0.35)] active:scale-[0.99]',
                  'flex items-center justify-center gap-2',
                  submitting && 'opacity-70 cursor-not-allowed'
                )}
              >
                <Send className="w-5 h-5" />
                {submitting ? 'Sending...' : 'Send Message'}
              </button>

              <p className="text-center font-montserrat text-xs text-white/60">
                Your info stays with us — no spam, no sharing.
              </p>
            </form>
          </div>
        </div>
      </SectionShell>

      <MemoizedSlideInToast
        open={toastOpen}
        onClose={handleToastClose}
        variant={submitStatus?.ok ? 'success' : 'error'}
        title={submitStatus?.ok ? 'Message sent' : submitStatus ? 'Error' : undefined}
        message={submitStatus?.msg}
      />
    </>
  );
}